import { createContext, useContext, useEffect, type ReactNode } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { organizationBrowseQueries } from '../../api/organizationBrowseQueries'
import { useUserContext } from '../../providers/UserContextProvider'
import { LoadingState } from '../../components/ui/PageState'

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

const UnitIdContext = createContext<string | undefined>(undefined)

/** The org-wide unit tree, under the key the team and command-centre pages already read it by. */
function useUnits(wanted = true) {
  const { i18n } = useTranslation()
  const { primaryContext, adminOrganizationId } = useUserContext()
  const organizationId = primaryContext?.organizationId ?? adminOrganizationId
  return useQuery({
    ...organizationBrowseQueries.units(organizationId ?? '', undefined, false, i18n.language),
    enabled: wanted && !!organizationId,
  })
}

/** The `:id` of a unit route as the unit's id, whether the address named it by id or by slug. */
export function useUnitIdParam(): string | undefined {
  const { id } = useParams<{ id: string }>()
  return useContext(UnitIdContext) ?? id
}

/** The path segment a link to this unit should use: its slug once the tree is in, its id until then. */
export function useUnitSegment(id: string): string {
  const { data: units } = useUnits(!!id)
  const slug = units?.find((unit) => unit.id === id)?.slug
  // A stored slug may hold `/`, `?` or `#`; unencoded, they would end the segment.
  return slug ? encodeURIComponent(slug) : id
}

/**
 * `/teams/dispatch-optimization` rather than `/teams/decade00-…`.
 *
 * A slug is looked up in the unit tree and handed to the page as the id its
 * queries key on. An id is swapped for the slug in the address bar, so what
 * gets copied and pasted reads as a name, while every link already shared --
 * and the many this app still builds from an id -- keeps working. The slug
 * survives a rename (OrgUnitService leaves it alone), so a bookmark does too.
 */
export function UnitAddress({ children }: { children: ReactNode }) {
  const { id: ref } = useParams<{ id: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { data: units, isLoading } = useUnits()
  const byId = !ref || UUID.test(ref)
  const unit = units?.find((candidate) => (byId ? candidate.id === ref : candidate.slug === ref))

  useEffect(() => {
    if (!byId || !ref || !unit?.slug) return
    const { pathname, search, hash, state } = location
    navigate(pathname.replace(`/${ref}`, `/${encodeURIComponent(unit.slug)}`) + search + hash, { replace: true, state })
  }, [byId, ref, unit?.slug, location, navigate])

  // An id needs no lookup, so it renders at once, as it did before slugs.
  if (!byId && isLoading) {
    return <LoadingState title={t('teams.loadingEntityTitle')} description={t('teams.loadingEntityDescription')} />
  }
  // An unknown slug goes through as it is, and the page shows its own "not found".
  return <UnitIdContext.Provider value={unit?.id ?? ref}>{children}</UnitIdContext.Provider>
}
